import { CalendarRange } from 'lucide-react';

import { SCOPE_LABELS } from '@/components/destinations/utils';
import {
  Checkbox,
  CheckboxField,
  CheckboxGroup,
} from '@/components/ui/catalyst/checkbox';
import { Description, Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Radio, RadioField, RadioGroup } from '@/components/ui/catalyst/radio';
import { useDestinations } from '@/hooks/useDestinations';
import type { DestinationScope } from '@/types/provider-destinations';
import { RequestTemplateIntent } from '@/types/provider-destinations';

import type { WizardScopeSelection } from './useCreateRequestWizard';

// ---------------------------------------------------------------------------
// Coverage indicator
// ---------------------------------------------------------------------------

const COVERAGE_DOT: Record<string, string> = {
  OK: 'bg-emerald-500',
  MISSING: 'bg-zinc-300',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ScopeSelectionStepProps {
  scopeSelections: WizardScopeSelection[];
  templateType: RequestTemplateIntent;
  providerServiceId: string | null | undefined;
  onToggleScope: (scope: DestinationScope) => void;
  onSetTemplateType: (t: RequestTemplateIntent) => void;
  serviceStartDate: string;
  serviceEndDate: string;
  onSetServiceStartDate: (value: string) => void;
  onSetServiceEndDate: (value: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScopeSelectionStep = ({
  scopeSelections,
  templateType,
  providerServiceId,
  onToggleScope,
  onSetTemplateType,
  serviceStartDate,
  serviceEndDate,
  onSetServiceStartDate,
  onSetServiceEndDate,
}: ScopeSelectionStepProps) => {
  const { getScopeDestinations } = useDestinations(providerServiceId ?? '');

  return (
    <div className="space-y-6">
      {/* Scope checkboxes */}
      <Field>
        <Label>Select record types to request</Label>
        <Description>
          Choose which types of records you need from this provider.
        </Description>

        <CheckboxGroup className="mt-3">
          {scopeSelections.map((sel) => {
            const block = providerServiceId
              ? getScopeDestinations(sel.scope)
              : undefined;
            const coverageStatus = block?.coverage_status ?? 'MISSING';

            return (
              <CheckboxField key={sel.scope}>
                <Checkbox
                  checked={sel.selected}
                  onChange={() => onToggleScope(sel.scope)}
                  color="indigo"
                />
                <Label>
                  <span className="flex items-center gap-2">
                    {SCOPE_LABELS[sel.scope]}
                    {providerServiceId && (
                      <span
                        className={`inline-block size-2 rounded-full ${COVERAGE_DOT[coverageStatus]}`}
                        title={`Coverage: ${coverageStatus.toLowerCase()}`}
                      />
                    )}
                  </span>
                </Label>
              </CheckboxField>
            );
          })}
        </CheckboxGroup>
      </Field>

      {/* Template type */}
      <Field>
        <Label>Request template</Label>
        <Description>
          Choose the template type based on the provider facility.
        </Description>

        <RadioGroup
          value={templateType}
          onChange={(v: string) =>
            onSetTemplateType(v as RequestTemplateIntent)
          }
          className="mt-3"
        >
          <RadioField>
            <Radio value={RequestTemplateIntent.STANDARD} color="indigo" />
            <Label>Standard</Label>
            <Description>
              For clinics, offices, and outpatient facilities.
            </Description>
          </RadioField>
          <RadioField>
            <Radio value={RequestTemplateIntent.HOSPITAL} color="indigo" />
            <Label>Hospital</Label>
            <Description>
              For hospitals and inpatient facilities.
            </Description>
          </RadioField>
        </RadioGroup>
      </Field>

      {/* Dates of Service */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-zinc-700">
          <CalendarRange className="size-4" />
          Dates of Service
        </div>
        <div className="flex items-center gap-3">
          <Field className="flex-1">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={serviceStartDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onSetServiceStartDate(e.target.value)
              }
            />
          </Field>
          <span className="mt-5 text-sm text-zinc-500">&mdash;</span>
          <Field className="flex-1">
            <Label>End Date</Label>
            <Input
              value={serviceEndDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onSetServiceEndDate(e.target.value)
              }
              placeholder="MM/DD/YYYY or Present"
            />
          </Field>
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Defaults to Date of Loss when available. Auto-filled from provider
          encounters when left blank.
        </p>
      </div>
    </div>
  );
};
